import React, { useEffect } from 'react';
import _get from 'lodash/get';
import PropTypes from 'prop-types';
import { useFormContext } from 'react-hook-form';

import { DropdownItem, DropdownMenu, DropdownToggle } from 'components/Dropdown2';
import { ErrorMessage, Dropdown as FormDropdown, Switch } from 'components/HookForm';
import Message from 'components/Message';
import { bem } from 'helpers/class-name';

import { NONE_VALUE, RULES_BLOCK_MODE, clampSelectionValue, selectionBounds } from './RulesBlock.helpers';
import messages from './RulesBlock.messages';
import './RulesBlock.scss';

const { block, element } = bem('sku-rules-block');

const SELECTION_FIELDS = ['minSelection', 'maxSelection'];

const RulesBlock = ({ mode, name, options }) => {
  const {
    formState: { errors },
    getValues,
    setValue,
    watch,
  } = useFormContext();
  const disabled = mode === RULES_BLOCK_MODE.READ_ONLY;
  const optionCount = options.length;
  const fieldName = field => `${name}.${field}`;

  const allowsMultiple = watch(fieldName('allowsMultiple'));
  const hasDefault = watch(fieldName('hasDefault'));
  const minSelection = watch(fieldName('minSelection'));

  useEffect(() => {
    if (disabled) return;

    SELECTION_FIELDS.forEach(field => {
      const current = getValues(fieldName(field));
      const clamped = clampSelectionValue(current, optionCount);
      if (clamped !== current) setValue(fieldName(field), clamped);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [optionCount, disabled]);

  const optionIds = options.map(option => option.id).join('|');
  useEffect(() => {
    if (disabled) return;

    const currentDefault = getValues(fieldName('defaultOptionId'));
    if (currentDefault && !options.some(option => option.id === currentDefault)) {
      setValue(fieldName('defaultOptionId'), '');
      setValue(fieldName('hasDefault'), false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [optionIds, disabled]);

  const bounds = selectionBounds(optionCount);
  const minNumeric = minSelection === NONE_VALUE || minSelection === '' ? 1 : Number(minSelection);
  const maxBounds = bounds.filter(value => value >= minNumeric);

  const handleMinSelect = (onChange, value) => {
    onChange(value);
    if (value !== NONE_VALUE) {
      const currentMax = getValues(fieldName('maxSelection'));
      if (currentMax !== NONE_VALUE && Number(currentMax) < Number(value)) {
        setValue(fieldName('maxSelection'), Number(value));
      }
    }
  };

  const renderToggle = (field, label) => (
    <Switch name={fieldName(field)} className={element('rule-toggle')} disabled={disabled} variant="brand">
      {() => (
        <span className={element('rule-label')}>
          <Message message={label} />
        </span>
      )}
    </Switch>
  );

  const renderSelectionDropdown = (field, values, onSelect) => (
    <FormDropdown name={fieldName(field)} className={element('pill-dropdown')} disabled={disabled}>
      {({ value, onChange }) => (
        <>
          <DropdownToggle isRoundRadiusStyle className={element('pill-toggle')}>
            {value === NONE_VALUE || value === '' ? <Message message={messages.noneOption} /> : value}
          </DropdownToggle>
          <DropdownMenu>
            <DropdownItem isSelected={value === NONE_VALUE} onClick={() => onSelect(onChange, NONE_VALUE)}>
              <Message message={messages.noneOption} />
            </DropdownItem>
            {values.map(optionValue => (
              <DropdownItem
                key={optionValue}
                isSelected={value === optionValue}
                onClick={() => onSelect(onChange, optionValue)}>
                {optionValue}
              </DropdownItem>
            ))}
          </DropdownMenu>
        </>
      )}
    </FormDropdown>
  );

  return (
    <div className={block()}>
      <h3 className={element('title')}>
        <Message message={messages.title} />
      </h3>

      <div className={element('rule', { error: !!_get(errors, fieldName('isRequired')) })}>
        {renderToggle('isRequired', messages.requiredToggle)}
        <ErrorMessage name={fieldName('isRequired')} errors={errors} />
      </div>

      <div className={element('rule')}>
        {renderToggle('allowsMultiple', messages.multipleToggle)}
        {allowsMultiple && (
          <div className={element('sub-rows')}>
            <div className={element('sub-row')}>
              <span className={element('sub-row-label')}>
                <Message message={messages.minSelectionLabel} />
              </span>
              {renderSelectionDropdown('minSelection', bounds, handleMinSelect)}
            </div>
            <div className={element('sub-row')}>
              <span className={element('sub-row-label')}>
                <Message message={messages.maxSelectionLabel} />
              </span>
              {renderSelectionDropdown('maxSelection', maxBounds, (onChange, value) => onChange(value))}
            </div>
          </div>
        )}
      </div>

      <div className={element('rule')}>{renderToggle('allowsQuantities', messages.quantitiesToggle)}</div>

      <div className={element('rule')}>
        {renderToggle('hasDefault', messages.defaultToggle)}
        {hasDefault && (
          <div className={element('default-row')}>
            <FormDropdown
              name={fieldName('defaultOptionId')}
              className={element('default-dropdown')}
              disabled={disabled}>
              {({ value, onChange }) => {
                const selected = options.find(option => option.id === value);
                return (
                  <>
                    <DropdownToggle className={element('default-toggle')}>
                      {selected ? (
                        selected.name
                      ) : (
                        <span className={element('placeholder')}>
                          <Message message={messages.defaultPlaceholder} />
                        </span>
                      )}
                    </DropdownToggle>
                    <DropdownMenu>
                      {options.map(option => (
                        <DropdownItem
                          key={option.id}
                          isSelected={option.id === value}
                          onClick={() => onChange(option.id)}>
                          {option.name}
                        </DropdownItem>
                      ))}
                    </DropdownMenu>
                  </>
                );
              }}
            </FormDropdown>
          </div>
        )}
      </div>
    </div>
  );
};

RulesBlock.defaultProps = {
  mode: RULES_BLOCK_MODE.CREATE,
  name: 'rules',
  options: [],
};

RulesBlock.propTypes = {
  mode: PropTypes.oneOf(Object.values(RULES_BLOCK_MODE)),
  name: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      isDefault: PropTypes.bool,
      name: PropTypes.string,
      ordinal: PropTypes.number,
    })
  ),
};

export default RulesBlock;
